import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Rocket, Phone, Copy, ArrowRight, CheckCircle2, Share2,
  MessageSquare, BarChart3,
} from 'lucide-react';
import axios from 'axios';
import confetti from 'canvas-confetti';

const API = process.env.REACT_APP_BACKEND_URL;

export default function OnboardingSuccessPage() {
  const { token, fetchMe } = useAuth();
  const navigate = useNavigate();
  const [phoneDisplay, setPhoneDisplay] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/api/onboarding/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPhoneNumber(data.ai_number || data.provision?.phone_number || '');
        setPhoneDisplay(data.ai_number_display || data.provision?.phone_display || data.ai_number || '');
      } catch { /* ignore */ }
      // Refresh user context so onboarding_complete updates
      try { await fetchMe(); } catch {}
    };
    load();

    try {
      confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
      setTimeout(() => confetti({ particleCount: 50, spread: 90, origin: { y: 0.5, x: 0.3 } }), 300);
      setTimeout(() => confetti({ particleCount: 50, spread: 90, origin: { y: 0.5, x: 0.7 } }), 500);
    } catch { /* confetti not available */ }
  }, [token]);

  const copyNumber = () => {
    navigator.clipboard.writeText(phoneNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-[#0a0a14] text-foreground flex items-center justify-center p-4" data-testid="activation-success-page">
      <div className="max-w-md w-full space-y-6 text-center">
        {/* Success Icon */}
        <div className="relative inline-block">
          <div className="absolute inset-0 bg-emerald-500/20 rounded-full blur-2xl animate-pulse" />
          <div className="relative h-20 w-20 rounded-full bg-emerald-500/10 border-2 border-emerald-500/30 flex items-center justify-center mx-auto">
            <Rocket size={36} className="text-emerald-400" />
          </div>
        </div>

        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground" data-testid="success-title">
            Your AI is Now Live!
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Your AI assistant is answering calls right now. Try it out below.
          </p>
        </div>

        {/* Phone Number */}
        <Card className="border-emerald-500/20 bg-gradient-to-br from-primary/5/30 to-card">
          <CardContent className="p-6">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2 font-medium">Your AI Phone Number</p>
            <p className="text-2xl sm:text-3xl font-mono font-bold text-foreground" data-testid="success-phone-number">
              {phoneDisplay || 'Loading...'}
            </p>
            <div className="flex items-center justify-center gap-3 mt-4">
              <Button size="sm" variant="outline" onClick={copyNumber}
                className="border-border text-foreground hover:text-foreground gap-1.5" data-testid="copy-number-btn">
                {copied ? <CheckCircle2 size={12} className="text-emerald-400" /> : <Copy size={12} />}
                {copied ? 'Copied!' : 'Copy'}
              </Button>
              <Button size="sm" variant="outline"
                className="border-border text-foreground hover:text-foreground gap-1.5"
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({ title: 'My AI Real Estate Assistant', text: `Call my AI assistant at ${phoneDisplay}` });
                  } else { copyNumber(); }
                }} data-testid="share-number-btn">
                <Share2 size={12} /> Share
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* First Value CTAs */}
        <div className="space-y-3">
          <Card className="border-sky-500/20 bg-sky-500/5 hover:bg-sky-500/8 transition-colors cursor-pointer"
            onClick={() => navigate('/call-simulation')} data-testid="test-simulation-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-sky-500/15 flex items-center justify-center shrink-0">
                <MessageSquare size={20} className="text-sky-400" />
              </div>
              <div className="text-left flex-1">
                <p className="text-sm font-semibold text-foreground">Test your AI now</p>
                <p className="text-[11px] text-muted-foreground">Run a quick simulation — see how it handles buyers, sellers & objections</p>
              </div>
              <ArrowRight size={16} className="text-sky-400 shrink-0" />
            </CardContent>
          </Card>

          <Card className="border-border bg-card/80 hover:bg-muted transition-colors cursor-pointer"
            onClick={() => { if (phoneNumber) { window.location.href = `tel:${phoneNumber}`; } }}
            data-testid="call-ai-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-emerald-500/15 flex items-center justify-center shrink-0">
                <Phone size={20} className="text-emerald-400" />
              </div>
              <div className="text-left flex-1">
                <p className="text-sm font-semibold text-foreground">Call your AI</p>
                <p className="text-[11px] text-muted-foreground">Dial {phoneDisplay || 'your number'} and hear it live</p>
              </div>
              <ArrowRight size={16} className="text-muted-foreground shrink-0" />
            </CardContent>
          </Card>
        </div>

        <Button onClick={() => navigate('/analytics')} variant="outline"
          className="w-full border-border text-foreground hover:text-foreground h-10 text-sm gap-2"
          data-testid="goto-dashboard-btn">
          <BarChart3 size={14} /> Go to Dashboard <ArrowRight size={14} />
        </Button>

        <p className="text-xs text-muted-foreground">
          Share your number to start receiving leads. Your AI handles the rest.
        </p>
      </div>
    </div>
  );
}
